import { useId, type ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import FeatherIcon from 'components/base/FeatherIcon';

export interface WorkspaceItemLabelProps {
  // Feather icon name (matches the navigation bar); rendered before the name,
  // inheriting the Norscom-green text colour.
  readonly icon?: string;
  readonly name: string;
  // The tooltip content shown on hover (omit for no tooltip). The per-type
  // label components pass a `WorkspaceItemTooltip` here.
  readonly children?: ReactNode;
}

/**
 * Presentational item label: a Norscom-green name with an optional leading icon
 * and an optional hover tooltip (the whole label is the trigger — no info icon).
 * Shared by every per-type label component so the tree and the contents table
 * render names identically. Internal — callers use the per-type components /
 * `WorkspaceItemLabelFor`.
 */
export const WorkspaceItemLabel = ({ icon, name, children }: WorkspaceItemLabelProps) => {
  const tooltipId = useId();
  const label = (
    <span className="workspace-item-name">
      {icon && <FeatherIcon icon={icon} size={14} className="workspace-item-icon" />}
      {name}
    </span>
  );
  if (!children) return label;
  return (
    <OverlayTrigger
      placement="right"
      overlay={
        <Tooltip id={tooltipId} className="workspace-item-tooltip-overlay">
          {children}
        </Tooltip>
      }
    >
      {label}
    </OverlayTrigger>
  );
};
